import { useState, useMemo, useEffect, useCallback } from 'react';
import { format, addDays } from 'date-fns';
import { Plus, RefreshCw, Building2, Sparkles, Calendar, Bed, Filter, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Check, X, CheckCircle2, XCircle, Minus, ArrowUp, ArrowDown, Link2, Grid3X3, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fetchCompetitorPrices } from '@/services/competitorPricing';
import { Competitor, PricingAnalysisRow, OTABreakdown, OTA_CHANNEL_COLORS, SuggestionStatus, MarketStatus, DEFAULT_PRICING_STRATEGY, OTA_CHANNELS, CompetitorRawData } from '@/types/competitor';
import competitorRawData from '@/data/competitor_raw_data.json';
import { OTAChannel, Property } from '@/types/revenue-manager';
import { RoomInventoryItem } from '@/types/room-inventory';
import { AddCompetitorDialog } from './AddCompetitorDialog';
import { CompetitorCard } from './CompetitorCard';
import { PriceComparisonChart } from './PriceComparisonChart';
import { CompetitorHeatmapGrid } from './CompetitorHeatmapGrid';
import { CompetitorBreakdown } from './CompetitorBreakdown';
import { CompetitorPriceComparison } from './CompetitorPriceComparison';
import { AnalysisRoomMappingDialog, RoomMapping, getCompetitorRooms, CompetitorRoom } from './AnalysisRoomMappingDialog';
import { useRoomMappings, useSaveRoomMappings } from '@/hooks/use-room-mappings';
import { saveCompetitor, fetchCompetitors, updateCompetitor, deleteCompetitor, fetchPricePredictionJobs, updatePricePredictionJob, fetchPricePredictionAnalysis, PricePredictionJob, genericPatch, API_ENDPOINTS, triggerRoomTypeCompetitor } from '@/api/generic';
import { apiClient } from '@/api/client';
import { useCurrencyFormat } from '@/hooks/use-currency-format';

// PricePredictionJob is imported from generic.ts - adding additional properties to the imported type
declare module '@/api/generic' {
  interface PricePredictionJob {
    org_id: number;
    property_mapping_json?: string;
    prediction_json_s3_key?: string;
    createdAt: string;
    updatedAt: string;
    created_by?: number;
  }
}

interface CompetitorsViewProps {
  property: Property;
  propertyId: string;
  propertyName?: string;
  basePrice: number;
  otaChannels: OTAChannel[];
  roomTypes?: RoomInventoryItem[];
  branches: Array<{ id: number; cm_property_id: string; branch_name: string; org_id?: number }>;
}

type SubTab = 'analysis' | 'manage';
type ViewMode = 'table' | 'heatmap';

// Smart pricing algorithm
function calculateSuggestedPrice(
  yourPrice: number,
  competitorAvg: number,
  occupancy: number,
  strategy = DEFAULT_PRICING_STRATEGY
): { price: number; strategy: 'maintain' | 'increase' | 'decrease'; reasoning: string } {
  const priceDiff = yourPrice - competitorAvg;
  const priceDiffPercent = (priceDiff / competitorAvg) * 100;
  const { low: lowOccupancy, high: highOccupancy } = strategy.occupancyThresholds;

  let suggestedPrice = yourPrice;
  let priceStrategy: 'maintain' | 'increase' | 'decrease' = 'maintain';
  let reasoning = '';

  // Rule-based pricing logic
  if (priceDiffPercent > 10 && occupancy < lowOccupancy) {
    // Overpriced and low occupancy → decrease
    const reduction = strategy.aggressiveness === 'aggressive' ? 0.15 : strategy.aggressiveness === 'moderate' ? 0.10 : 0.05;
    suggestedPrice = Math.round(yourPrice * (1 - reduction));
    priceStrategy = 'decrease';
    reasoning = `Price is ${Math.abs(priceDiffPercent).toFixed(0)}% above market with low occupancy (${occupancy}%). Recommend price reduction to boost bookings.`;
  } else if (priceDiffPercent < -10 && occupancy > highOccupancy) {
    // Underpriced and high occupancy → increase
    const increase = strategy.aggressiveness === 'aggressive' ? 0.20 : strategy.aggressiveness === 'moderate' ? 0.15 : 0.10;
    suggestedPrice = Math.round(yourPrice * (1 + increase));
    priceStrategy = 'increase';
    reasoning = `Strong demand with ${occupancy}% occupancy while priced ${Math.abs(priceDiffPercent).toFixed(0)}% below market. Opportunity to increase revenue.`;
  } else if (occupancy > highOccupancy && priceDiffPercent >= -10 && priceDiffPercent <= 10) {
    // High occupancy with market-aligned price → slight increase
    const increase = strategy.aggressiveness === 'aggressive' ? 0.10 : strategy.aggressiveness === 'moderate' ? 0.05 : 0.03;
    suggestedPrice = Math.round(yourPrice * (1 + increase));
    priceStrategy = 'increase';
    reasoning = `High demand at ${occupancy}% occupancy. Price is aligned with market - modest increase recommended.`;
  } else if (occupancy < lowOccupancy && priceDiffPercent >= -10 && priceDiffPercent <= 10) {
    // Low occupancy with market-aligned price → slight decrease
    const reduction = strategy.aggressiveness === 'aggressive' ? 0.08 : strategy.aggressiveness === 'moderate' ? 0.05 : 0.03;
    suggestedPrice = Math.round(yourPrice * (1 - reduction));
    priceStrategy = 'decrease';
    reasoning = `Low demand at ${occupancy}% occupancy. Consider slight price reduction to attract more bookings.`;
  } else {
    reasoning = 'Market conditions stable. Current pricing is optimal for the demand level.';
  }

  // Apply minimum margin constraint
  const minPrice = competitorAvg * (1 - strategy.minMarginPercent / 100);
  if (suggestedPrice < minPrice && priceStrategy === 'decrease') {
    suggestedPrice = Math.round(minPrice);
    reasoning += ` Price floor applied to maintain ${strategy.minMarginPercent}% margin.`;
  }

  return { price: suggestedPrice, strategy: priceStrategy, reasoning };
}

function getMarketStatus(yourPrice: number, marketAvg: number): MarketStatus {
  const diffPercent = ((yourPrice - marketAvg) / marketAvg) * 100;
  if (diffPercent < -5) return 'underpriced';
  if (diffPercent > 5) return 'overpriced';
  return 'aligned';
}

export function CompetitorsView({ propertyId, propertyName = '', basePrice, otaChannels, roomTypes = [], branches = [], property }: CompetitorsViewProps) {
  const { toast } = useToast();
  const { format: formatPrice } = useCurrencyFormat();
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('analysis');
  const [addCompetitorOpen, setAddCompetitorOpen] = useState(false);
  
  // Find the branch using the property ID (which could be cm_property_id or string branch.id)
  const selectedBranch = branches.find(b => b.cm_property_id === propertyId || String(b.id) === propertyId);
  const branchId = selectedBranch ? selectedBranch.id : 0;
  const { data: savedMappings, isLoading: isLoadingMappings } = useRoomMappings(branchId);
  const saveMappingsMutation = useSaveRoomMappings(branchId, propertyName);
  const [analysisRoomMappings, setAnalysisRoomMappings] = useState<RoomMapping[]>([]);
  // Store the competitor room data extracted from API for room mappings
  const [competitorRoomsForMapping, setCompetitorRoomsForMapping] = useState<CompetitorRoom[]>([]);
  
  // State for competitors and loading status
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [isLoadingCompetitors, setIsLoadingCompetitors] = useState<boolean>(false);
  const [editingCompetitor, setEditingCompetitor] = useState<Competitor | null>(null);
  
  // State for price prediction jobs
  const [pricePredictionJob, setPricePredictionJob] = useState<PricePredictionJob | null>(null);
  const [isLoadingPredictionJob, setIsLoadingPredictionJob] = useState<boolean>(false);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  
  // State for analysis parameters
  const today = new Date();
  const [startDate, setStartDate] = useState<Date>(today);
  const [numberOfDays, setNumberOfDays] = useState<number>(15);
  
  // Analysis tab additional states
  const [roomType, setRoomType] = useState('all');
  const [roomVariant, setRoomVariant] = useState('all');
  const [otaChannel, setOtaChannel] = useState('all');
  const [viewMode, setViewMode] = useState<'heatmap' | 'table'>('heatmap');
  const [analysisMappingDialogOpen, setAnalysisMappingDialogOpen] = useState(false);
  const [pricingAnalysisData, setPricingAnalysisData] = useState<PricingAnalysisRow[]>([]);
  const [isLoadingPricingData, setIsLoadingPricingData] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [suggestionStatuses, setSuggestionStatuses] = useState<Record<string, 'accepted' | 'rejected' | undefined>>({});
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  
  // Available variants and OTA channels for filtering
  const availableVariants = [
    { id: 1, name: 'Standard Room', roomTypeName: 'Standard' },
    { id: 2, name: 'Deluxe Room', roomTypeName: 'Deluxe' },
    { id: 3, name: 'Suite', roomTypeName: 'Suite' },
  ];
  
  // Compute active competitors
  const activeCompetitors = useMemo(() => {
    return competitors.filter(c => c.is_active);
  }, [competitors]);
  
  // Fetch pricing data for analysis using the API
  const fetchPricingData = useCallback(async () => {
    if (!pricePredictionJob) {
      toast({
        title: "No prediction job found",
        description: "Unable to fetch analysis data without a prediction job.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoadingPricingData(true);
    try {
      console.log('Fetching pricing data for job:', pricePredictionJob.job_id);
      
      // Call the prediction analysis API with the job ID
      const response = await fetchPricePredictionAnalysis(pricePredictionJob.job_id);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch analysis data');
      }
      
      // Get daily data or use an empty array if not available
      const daily_data = Array.isArray(response.data) ? response.data : (response.data.daily_data || []);
      const property_mappings = response.data.property_mappings || {};
      
      console.log('Received analysis data:', response.data);
      
      // Transform API data to match expected format
      const analysisData = daily_data.map((day: any) => {
        // Extract date from API data
        const date = day.date || '';
        const dayOfWeek = day.dayOfWeek || (date ? format(new Date(date), 'EEE') : '');
        const roomType = day.roomType || '';
        const variantId = day.variantId || '';
        const variantName = day.variantName || '';
        
        // Extract pricing data - use the appropriate field names from the JSON
        const yourPrice = day.yourPrice || basePrice;
        const marketAvg = day.marketAvg || 0;
        const marketAvgDiff = day.marketAvgDiff || (marketAvg - yourPrice);
        const occupancy = day.occupancy !== undefined ? day.occupancy : 50;
        
        // Get suggested price or calculate it
        const suggestedPrice = day.suggestedPrice || day.suggested || calculateSuggestedPrice(yourPrice, marketAvg, occupancy).price;
        const change = day.change !== undefined ? day.change : (suggestedPrice - yourPrice);
        const changePercent = day.changePercent !== undefined ? day.changePercent : 
                             (yourPrice > 0 ? ((suggestedPrice - yourPrice) / yourPrice) * 100 : 0);
        
        // Format price values for display
        const formattedYourPrice = formatPrice(yourPrice);
        const formattedMarketAvg = formatPrice(marketAvg);
        const formattedSuggestedPrice = formatPrice(suggestedPrice);
        const formattedChange = formatPrice(Math.abs(change));
        
        // Get strategy and market status from data if available
        const strategy = day.strategy || (suggestedPrice > yourPrice ? 'increase' : 
                      suggestedPrice < yourPrice ? 'decrease' : 'maintain');
        const marketStatus = day.marketStatus || getMarketStatus(yourPrice, marketAvg);
        const confidence = day.confidence || 'medium';
        
        // Extract competitors information if available
        const competitors = day.competitors || [];
        
        // Format channel prices from API data or create defaults
        const channelPrices = day.channelPrices || {
          bookingCom: marketAvg,
          expedia: marketAvg,
          hotelsCom: marketAvg,
          airbnb: marketAvg,
        };
        
        // Calculate differences if not provided, round to 2 decimal places
        const channelDiff = day.channelDiff || {
          bookingCom: parseFloat(((channelPrices.bookingCom || 0) - yourPrice).toFixed(2)),
          expedia: parseFloat(((channelPrices.expedia || 0) - yourPrice).toFixed(2)),
          hotelsCom: parseFloat(((channelPrices.hotelsCom || 0) - yourPrice).toFixed(2)),
          airbnb: parseFloat(((channelPrices.airbnb || 0) - yourPrice).toFixed(2)),
        };
        
        return {
          date,
          dayOfWeek,
          roomType,
          variantId,
          variantName,
          yourPrice,
          marketAvg,
          marketAvgDiff,
          suggestedPrice,
          occupancy,
          change,
          changePercent,
          strategy,
          confidence,
          marketStatus,
          competitors,
          // Formatted price values for display
          formattedYourPrice,
          formattedMarketAvg,
          formattedSuggestedPrice,
          formattedChange,
          aiReasoning: day.aiReasoning || day.reasoning || 
                     `Suggested price based on market average (${formatPrice(marketAvg)}) and occupancy (${occupancy}%).`,
          suggestionStatus: day.suggestionStatus || suggestionStatuses[date] || 'pending',
          channelPrices,
          channelDiff,
        };
      });
      
      // Note: property_mappings from API is not in RoomMapping format
      // We don't update analysisRoomMappings here as it expects RoomMapping[] type
      if (property_mappings && Object.keys(property_mappings).length > 0) {
        console.log('Property mappings received from API:', property_mappings);
      }
      
      setPricingAnalysisData(analysisData);
      // toast({
      //   title: "Analysis data refreshed",
      //   description: `Loaded ${analysisData.length} days of pricing data for analysis`,
      // });
      console.log(`Loaded ${analysisData.length} days of pricing data for analysis`)
    } catch (error) {
      console.error('Error fetching pricing analysis data:', error);
      toast({
        title: "Failed to load analysis data",
        description: error instanceof Error ? error.message : "There was an error fetching analysis data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPricingData(false);
    }
  }, [pricePredictionJob, basePrice, suggestionStatuses, toast, formatPrice]);
  
  // Handle suggestion acceptance/rejection
  const handleAcceptSuggestion = useCallback((date: string) => {
    setSuggestionStatuses(prev => {
      const wasRejected = prev[date] === 'rejected';
      const wasAccepted = prev[date] === 'accepted';
      
      if (!wasAccepted) {
        setAcceptedCount(count => count + 1);
      }
      
      if (wasRejected) {
        setRejectedCount(count => count - 1);
      }
      
      return { ...prev, [date]: 'accepted' };
    });
  }, []);
  
  const handleRejectSuggestion = useCallback((date: string) => {
    setSuggestionStatuses(prev => {
      const wasAccepted = prev[date] === 'accepted';
      const wasRejected = prev[date] === 'rejected';
      
      if (!wasRejected) {
        setRejectedCount(count => count + 1);
      }
      
      if (wasAccepted) {
        setAcceptedCount(count => count - 1);
      }
      
      return { ...prev, [date]: 'rejected' };
    });
  }, []);
  
  // Handle bulk acceptance/rejection
  const handleAcceptAll = useCallback(() => {
    const newStatuses = { ...suggestionStatuses };
    selectedDates.forEach(date => {
      newStatuses[date] = 'accepted';
    });
    setSuggestionStatuses(newStatuses);
    setAcceptedCount(selectedDates.length);
    setRejectedCount(0);
    
    toast({
      title: "All suggestions accepted",
      description: `Accepted pricing for ${selectedDates.length} days`,
    });
  }, [selectedDates, suggestionStatuses, toast]);
  
  const handleRejectAll = useCallback(() => {
    const newStatuses = { ...suggestionStatuses };
    selectedDates.forEach(date => {
      newStatuses[date] = 'rejected';
    });
    setSuggestionStatuses(newStatuses);
    setRejectedCount(selectedDates.length);
    setAcceptedCount(0);
    
    toast({
      title: "All suggestions rejected",
      description: `Rejected pricing for ${selectedDates.length} days`,
    });
  }, [selectedDates, suggestionStatuses, toast]);
  
  // Handle date selection toggle
  const toggleDateSelection = useCallback((date: string) => {
    setSelectedDates(prev => 
      prev.includes(date) 
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  }, []);
  
  // Handle row expansion toggle for detailed view
  const toggleRowExpansion = useCallback((date: string) => {
    setExpandedRow(prev => prev === date ? null : date);
  }, []);
  
  // Handle applying price changes
  const handleApplyPriceChanges = useCallback(() => {
    const acceptedChanges = Object.entries(suggestionStatuses)
      .filter(([_, status]) => status === 'accepted')
      .map(([date]) => date);
    
    if (acceptedChanges.length === 0) {
      toast({
        title: "No changes to apply",
        description: "Please accept at least one price suggestion first.",
        variant: "destructive",
      });
      return;
    }
    
    toast({
      title: "Price changes applied",
      description: `Applied ${acceptedChanges.length} price changes`,
    });
    
    // In a real app, this would send the changes to an API
    console.log('Applying price changes for dates:', acceptedChanges);
  }, [suggestionStatuses, toast]);
  
  // Handle saving room mappings
  const handleSaveMappings = useCallback(async (mappings: RoomMapping[], competitorHotelName: string) => {
    setAnalysisRoomMappings(mappings);
    
    // Save to CompetitorRoomMapping table
    saveMappingsMutation.mutate({ competitorHotelName, mappings });
    
    // Also save to price prediction job if available
    if (pricePredictionJob) {
      try {
        // Transform mappings into the required format for price prediction job
        // Format: { "Your Room Variant Name": ["Competitor Room 1", "Competitor Room 2", ...] }
        const propertyMappingJson: Record<string, string[]> = {};
        
        mappings.forEach(mapping => {
          const key = mapping.yourVariantName;
          const competitorRoom = mapping.competitorRoomName;
          
          if (!propertyMappingJson[key]) {
            propertyMappingJson[key] = [];
          }
          
          // Add competitor room if not already in the array
          if (!propertyMappingJson[key].includes(competitorRoom)) {
            propertyMappingJson[key].push(competitorRoom);
          }
        });
        
        console.log('Saving property_mapping_json to price prediction job:', propertyMappingJson);
        
        // Update price prediction job with property_mapping_json
        const response = await apiClient.patch(`${API_ENDPOINTS.pricePredictionJob}/${pricePredictionJob.job_id}`, {
          property_mapping_json: JSON.stringify(propertyMappingJson)
        });
        
        if (response.data.success) {
          console.log('Successfully saved property_mapping_json to price prediction job');
          toast({
            title: 'Mappings saved',
            description: 'Room mappings saved to price prediction job',
          });
        }
      } catch (error) {
        console.error('Error saving property_mapping_json to price prediction job:', error);
        toast({
          title: 'Warning',
          description: 'Mappings saved but failed to update price prediction job',
          variant: 'destructive',
        });
      }
    }
  }, [saveMappingsMutation, pricePredictionJob, toast]);
  
  // Fetch competitors from the API
  const fetchCompetitorsData = useCallback(async () => {
    if (branchId === 0) {
      toast({
        title: "No branch selected",
        description: "Please select a valid branch to fetch competitors",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoadingCompetitors(true);
    try {
      const competitorsData = await fetchCompetitors(branchId);
      console.log('Fetched competitors:', competitorsData);
      setCompetitors(competitorsData);
      // toast({
      //   title: "Competitors loaded",
      //   description: `${competitorsData.length} competitors found for this branch`,
      // });
      console.log(`${competitorsData.length} competitors found for this branch`)
    } catch (error) {
      console.error('Error fetching competitors:', error);
      toast({
        title: "Failed to load competitors",
        description: "There was an error fetching competitors. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCompetitors(false);
    }
  }, [branchId, toast]);
  
  // Fetch price prediction job for the branch
  const fetchPricePredictionJobData = useCallback(async () => {
    if (branchId === 0) return;
    
    setIsLoadingPredictionJob(true);
    try {
      const jobs = await fetchPricePredictionJobs(branchId);
      console.log('Fetched price prediction jobs:', jobs);
      
      if (jobs.length > 0) {
        // Use the most recent job
        const job = jobs[0];
        setPricePredictionJob(job);
        
        // Parse property_mapping_json and convert to RoomMapping format
        if (job.property_mapping_json) {
          try {
            const propertyMappingJson = JSON.parse(job.property_mapping_json);
            console.log('Parsed property_mapping_json:', propertyMappingJson);
            
            // Convert from { "Your Room Variant": ["Competitor Room 1", "Competitor Room 2"] }
            // to RoomMapping[] format
            const mappings: RoomMapping[] = [];
            Object.entries(propertyMappingJson).forEach(([yourVariantName, competitorRooms]) => {
              if (Array.isArray(competitorRooms)) {
                competitorRooms.forEach((competitorRoomName: string) => {
                  mappings.push({
                    id: `mapping-${yourVariantName}-${competitorRoomName}-${Date.now()}-${Math.random()}`,
                    yourVariantId: 0, // Will be populated when room variants are loaded
                    yourVariantName: yourVariantName,
                    yourRoomTypeName: '', // Will be populated when room variants are loaded
                    competitorProperty: '', // Will be populated from competitor data
                    competitorRoomName: competitorRoomName,
                    competitorPlatform: 'booking', // Default platform
                    matchConfidence: 100, // Existing mappings are considered 100% confident
                    isManualOverride: true,
                    createdAt: new Date().toISOString(),
                  });
                });
              }
            });
            
            console.log('Converted mappings from property_mapping_json:', mappings);
            setAnalysisRoomMappings(mappings);
          } catch (error) {
            console.error('Error parsing property_mapping_json:', error);
          }
        } else {
          // No existing mappings, clear the state
          setAnalysisRoomMappings([]);
        }
      } else {
        console.log('No price prediction jobs found for this branch');
        setPricePredictionJob(null);
        setAnalysisRoomMappings([]);
      }
    } catch (error) {
      console.error('Error fetching price prediction jobs:', error);
      setPricePredictionJob(null);
      setAnalysisRoomMappings([]);
    } finally {
      setIsLoadingPredictionJob(false);
    }
  }, [branchId]);
  
  // Load competitors and price prediction job when component mounts or branchId changes
  useEffect(() => {
    fetchCompetitorsData();
    fetchPricePredictionJobData();
  }, [fetchCompetitorsData, fetchPricePredictionJobData]);
  
  // Debug log for active competitors and prediction job status
  useEffect(() => {
    const activeCompetitors = competitors.filter(c => c.is_active);
    console.log('Active competitors:', activeCompetitors.length, activeCompetitors);
    console.log('Price prediction job status:', { 
      exists: !!pricePredictionJob,
      isLoading: isLoadingPredictionJob,
      isAnalyzing: isAnalyzing
    });
  }, [competitors, pricePredictionJob, isLoadingPredictionJob, isAnalyzing]);
  
  // Handle analyze action to update price prediction job with current competitor data
  const handleAnalyze = useCallback(async () => {
    if (!pricePredictionJob || branchId === 0) {
      toast({
        title: "Cannot start analysis",
        description: "No price prediction job found for this property.",
        variant: "destructive",
      });
      return;
    }
    
    // Validate inputs
    if (numberOfDays < 1) {
      toast({
        title: "Invalid number of days",
        description: "Please enter at least 1 day for analysis.",
        variant: "destructive",
      });
      return;
    }
    
    // Get active competitors for this branch
    const activeCompetitors = competitors.filter(c => c.is_active);
    if (activeCompetitors.length === 0) {
      toast({
        title: "No active competitors",
        description: "Please activate at least one competitor before analyzing.",
        variant: "destructive",
      });
      return;
    }
    
    // Prepare property OTA JSON with competitor data (competitor name as key, OTAs as array value)
    const propertyOtaJson = {};
    activeCompetitors.forEach(comp => {
      propertyOtaJson[comp.name] = comp.trackedOTAs;
    });
    
    // Log the format for debugging
    console.log('Property OTA JSON format:', JSON.stringify(propertyOtaJson, null, 2));
    
    setIsAnalyzing(true);
    try {
      // Format the selected start date in YYYY-MM-DD format
      const formattedStartDate = startDate.toISOString().split('T')[0];
      
      // Calculate end date by adding numberOfDays to start date
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + numberOfDays); // -1 because the start date counts as day 1
      const formattedEndDate = endDate.toISOString().split('T')[0];
      
      console.log(`Analyzing from ${formattedStartDate} to ${formattedEndDate} (${numberOfDays} days)`);
      
      
      // Update price prediction job with dynamic parameters
      const response = await updatePricePredictionJob(
        pricePredictionJob.job_id,
        JSON.stringify(propertyOtaJson),
        formattedStartDate,
        formattedEndDate,
        numberOfDays
      );
      
      // Also update the number of analytical days if needed
      if (response.success && pricePredictionJob.no_of_analytical_days !== numberOfDays) {
        // Use genericPatch directly since no_of_analytical_days isn't in updateCompetitor's interface
        try {
          const payload = [
            {
              resource_name: 'PricePredictionJob',
              column_value_pair: [
                {
                  id: pricePredictionJob.id,
                  data: {
                    no_of_analytical_days: numberOfDays
                  }
                }
              ]
            }
          ];
          
          // Use genericPatch function
          const daysResponse = await apiClient.patch(API_ENDPOINTS.generic, payload);
          if (!daysResponse.data.success) {
            console.warn('Failed to update number of analytical days');
          }
        } catch (err) {
          console.error('Error updating analytical days:', err);
        }
      }
      
      if (response.success) {
        toast({
          title: "Analysis started",
          description: `Analyzing competitors for ${numberOfDays} days starting ${formattedStartDate}.`,
        });
        // Refresh the job data
        await fetchPricePredictionJobData();
      } else {
        throw new Error('Failed to update price prediction job');
      }
    } catch (error) {
      console.error('Error updating price prediction job:', error);
      toast({
        title: "Analysis failed",
        description: "Failed to start competitor analysis. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  }, [branchId, competitors, pricePredictionJob, toast, fetchPricePredictionJobData, startDate, numberOfDays]);

  // Handle competitor actions
  const handleAddCompetitor = async (competitor: Omit<Competitor, 'id' | 'createdAt' | 'updatedAt'>) => {
    // Check if trying to add active competitor and max 2 are already active
    if (competitor.is_active && competitors.filter(c => c.is_active).length >= 2) {
      toast({
        title: 'Maximum active competitors reached',
        description: 'Only 2 competitors can be active at a time. Please deactivate one first.',
        variant: 'destructive',
      });
      return;
    }

    const newCompetitor: Competitor = {
      ...competitor,
      id: `comp-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    try {
      const response = await saveCompetitor(newCompetitor, branchId);
      
      if (response.success) {
        // Re-fetch the competitors to ensure UI is in sync with backend
        await fetchCompetitorsData();
        
        // Get active competitors including the new one if it's active
        const updatedActiveCompetitors = competitor.is_active 
          ? [...competitors.filter(c => c.is_active).map(c => c.name), competitor.name]
          : competitors.filter(c => c.is_active).map(c => c.name);
        
        // Trigger room type competitor creation API
        if (updatedActiveCompetitors.length > 0) {
          await triggerRoomTypeCompetitor(updatedActiveCompetitors);
        }
        
        toast({
          title: 'Competitor added',
          description: `${competitor.name} has been added successfully.`,
        });
      } else {
        throw new Error('Failed to add competitor');
      }
    } catch (error) {
      console.error('Error saving competitor:', error);
      toast({
        title: 'Error saving competitor',
        description: 'Failed to save competitor. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleEditCompetitor = (competitor: Competitor) => {
    setEditingCompetitor(competitor);
    setAddCompetitorOpen(true);
  };

  const handleUpdateCompetitor = async (competitor: Omit<Competitor, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!editingCompetitor) return;

    try {
      // Extract only the fields that need to be updated
      const updateFields = {
        name: competitor.name,
        location: competitor.location,
        distance: competitor.distance,
        distanceUnit: competitor.distanceUnit,
        starRating: competitor.starRating,
        category: competitor.category,
        rooms: competitor.rooms,
        amenities: competitor.amenities,
        trackedOTAs: competitor.trackedOTAs,
        is_active: competitor.is_active
      };
      
      // Use the updateCompetitor function with PATCH method
      const response = await updateCompetitor(editingCompetitor.id, updateFields);
      
      if (response.success) {
        // Re-fetch the competitors to ensure UI is in sync with backend
        await fetchCompetitorsData();
        
        // Calculate active competitors after this update
        const updatedActiveCompetitors = competitors
          .filter(c => c.id === editingCompetitor.id ? competitor.is_active : c.is_active)
          .map(c => c.id === editingCompetitor.id ? competitor.name : c.name);
        
        // Trigger room type competitor creation API
        if (updatedActiveCompetitors.length > 0) {
          await triggerRoomTypeCompetitor(updatedActiveCompetitors);
        }
        
        toast({
          title: 'Competitor updated',
          description: `${competitor.name} has been updated successfully.`,
        });
      } else {
        throw new Error('Failed to update competitor');
      }
    } catch (error) {
      console.error('Error updating competitor:', error);
      toast({
        title: 'Error updating competitor',
        description: 'Failed to update competitor. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteCompetitor = async (competitorId: string) => {
    const competitor = competitors.find(c => c.id === competitorId);
    if (!competitor) return;
    
    try {
      // Use the generic delete API to permanently delete the competitor
      const response = await deleteCompetitor(competitorId);
      
      if (response.success) {
        // Re-fetch the competitors to ensure UI is in sync with backend
        await fetchCompetitorsData();
        
        toast({
          title: 'Competitor deleted',
          description: `${competitor.name} has been removed.`,
        });
      } else {
        throw new Error('Failed to delete competitor');
      }
    } catch (error) {
      console.error('Error deleting competitor:', error);
      toast({
        title: 'Error deleting competitor',
        description: 'Failed to delete competitor. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleToggleCompetitor = async (competitorId: string) => {
    const competitor = competitors.find(c => c.id === competitorId);
    if (!competitor) return;
    
    // Check if trying to activate and max 2 are already active
    if (!competitor.is_active && competitors.filter(c => c.is_active).length >= 2) {
      toast({
        title: 'Maximum active competitors reached',
        description: 'Only 2 competitors can be active at a time. Please deactivate one first.',
        variant: 'destructive',
      });
      return;
    }

    const newActiveState = !competitor.is_active;

    try {
      // Use the updateCompetitor function with PATCH method
      const response = await updateCompetitor(competitorId, {
        is_active: newActiveState
      });
      
      if (response.success) {
        // Re-fetch the competitors to ensure UI is in sync with backend
        await fetchCompetitorsData();
        
        // Calculate active competitors after this toggle
        const updatedActiveCompetitors = competitors
          .filter(c => c.id === competitorId ? newActiveState : c.is_active)
          .map(c => c.name);
        
        // Trigger room type competitor creation API
        if (updatedActiveCompetitors.length > 0) {
          await triggerRoomTypeCompetitor(updatedActiveCompetitors);
        }
        
        toast({
          title: 'Competitor updated',
          description: `${competitor.name} is now ${newActiveState ? 'active' : 'inactive'}.`,
        });
      } else {
        throw new Error('Failed to update competitor');
      }
    } catch (error) {
      console.error('Error toggling competitor:', error);
      toast({
        title: 'Error updating competitor',
        description: 'Failed to update competitor status. Please try again.',
        variant: 'destructive',
      });
    }
  };
  
  // Helper function to get OTA breakdown for competitors
  const getOTABreakdownForCompetitors = useCallback((rowData: PricingAnalysisRow) => {
    // Check if competitors exist or if the row has competitors data
    if (!rowData.competitors || !rowData.competitors.length) {
      return [];
    }
    
    // Group competitors by property name
    const propertyGroups: Record<string, {
      property_name: string;
      platforms: { platform: string; price: number; roomType: string }[];
    }> = {};
    
    // Process and group all competitors by property_name
    rowData.competitors.forEach(comp => {
      const propName = comp.property_name;
      const price = typeof comp.price_per_night === 'number' ? 
        comp.price_per_night : 
        parseInt(comp.price_per_night, 10);
        
      if (!propertyGroups[propName]) {
        propertyGroups[propName] = {
          property_name: propName,
          platforms: []
        };
      }
      
      propertyGroups[propName].platforms.push({
        platform: comp.platform,
        price: price,
        roomType: comp.room_type_name || 'N/A'
      });
    });
    
    // Convert grouped properties to the format needed for display
    return Object.values(propertyGroups).map(group => {
      // Create competitor object for each property
      const competitor: Competitor = {
        id: `comp-${group.property_name.replace(/\s+/g, '-').toLowerCase()}`,
        name: group.property_name,
        is_active: true,
        trackedOTAs: group.platforms.map(p => p.platform.toLowerCase()),
        createdAt: new Date(),
        updatedAt: new Date(),
        location: 'From API',
        category: 'mid-range'
      };
      
      // Calculate average price across all platforms for this property
      const totalPrice = group.platforms.reduce((sum, p) => sum + p.price, 0);
      const avgPrice = Math.round(totalPrice / group.platforms.length);
      
      // Create OTA price breakdown
      const otaPrices = group.platforms.map(p => {
        // Get channel color from OTA_CHANNEL_COLORS or use a default
        const platformKey = p.platform.toLowerCase().replace(/\.com$/, '');
        const channelColor = OTA_CHANNEL_COLORS[platformKey] || '#888888';
        
        return {
          channel: p.platform,
          price: p.price,
          formattedPrice: formatPrice(p.price),
          channelColor,
          roomType: p.roomType
        };
      });
      console.log("competitor", {
        competitor,
        otaPrices,
        average: avgPrice,
        formattedAverage: formatPrice(avgPrice)
      })
      return {
        competitor,
        otaPrices,
        average: avgPrice,
        formattedAverage: formatPrice(avgPrice)
      };
    });
  }, [formatPrice]);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Competitor Intelligence</h2>
            <p className="text-sm text-muted-foreground">Manage competitors and analyze market pricing across OTAs</p>
          </div>
          
          {/* Analysis Controls */}
          <div className="flex items-center gap-3 bg-card p-2 rounded-lg border border-border">
            <div className="flex flex-col gap-1">
              <label htmlFor="start-date" className="text-xs text-muted-foreground">Start Date</label>
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-[180px] justify-start text-left font-normal"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            <div className="flex flex-col gap-1">
              <label htmlFor="days" className="text-xs text-muted-foreground">Days (1-30)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  id="days"
                  value={numberOfDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 1 && value <= 30) {
                      setNumberOfDays(value);
                    }
                  }}
                  min="1"
                  max="30"
                  className="w-16 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <div className="flex gap-1">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    className="h-9 px-2 py-1"
                    onClick={() => setNumberOfDays(7)}
                  >
                    7
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    className="h-9 px-2 py-1"
                    onClick={() => setNumberOfDays(15)}
                  >
                    15
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    className="h-9 px-2 py-1"
                    onClick={() => setNumberOfDays(30)}
                  >
                    30
                  </Button>
                </div>
              </div>
            </div>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      onClick={handleAnalyze}
                      variant="default"
                      size="sm"
                      disabled={isAnalyzing || !pricePredictionJob || competitors.filter(c => c.is_active).length === 0}
                      className="flex items-center gap-2 mt-5"
                    >
                      <Sparkles className="h-4 w-4" />
                      {isAnalyzing ? 'Analyzing...' : 'Start Analysis Now'}
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px] text-xs p-2">
                  {isAnalyzing ? 'Analysis in progress...' : 
                   !pricePredictionJob ? 'No price prediction job available for this property' : 
                   competitors.filter(c => c.is_active).length === 0 ? 'Please activate at least one competitor' : 
                   'Start competitor analysis'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
      
      {/* Analysis Job Status and Information */}
      {pricePredictionJob && (
        <Card className="mt-4 border-indigo-200">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium text-indigo-900">Analysis Job Information</CardTitle>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6" 
              onClick={fetchPricePredictionJobData}
              title="Refresh job information"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Job Status</p>
                  <p className="text-sm font-medium">
                    <Badge variant={pricePredictionJob.status === 'completed' ? 'default' : 
                           pricePredictionJob.status.includes('queue') ? 'outline' : 'secondary'}>
                      {pricePredictionJob.status}
                    </Badge>
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Last Updated</p>
                  <p className="text-sm font-medium">
                    {new Date(pricePredictionJob.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Room Mappings from Competitors to Property */}
              {pricePredictionJob.property_mapping_json && (
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Room Type Mappings</p>
                  <div className="max-h-60 overflow-y-auto space-y-3">
                    {Object.entries(JSON.parse(pricePredictionJob.property_mapping_json)).map(([competitorRoom, propertyRooms], index) => (
                      <div key={index} className="border-l-2 border-indigo-200 pl-3 py-1">
                        <p className="text-xs font-medium truncate">{competitorRoom}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Array.isArray(propertyRooms) && propertyRooms.map((room, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-indigo-50">
                              {room}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sub Tab Navigation */}
      <div className="flex items-center gap-1 rounded-xl border border-border bg-card p-1">
        <button
          onClick={() => setActiveSubTab('analysis')}
          className={cn(
            'flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200',
            activeSubTab === 'analysis'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          {numberOfDays}-Day Pricing Analysis
        </button>
        <button
          onClick={() => setActiveSubTab('manage')}
          className={cn(
            'flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200',
            activeSubTab === 'manage'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          Manage Competitors
        </button>
      </div>

      {/* Content */}
      {activeSubTab === 'analysis' ? (
        <div className="space-y-4">
          {/* Filters Card */}
          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-primary">{numberOfDays}-Day AI Dynamic Pricing</h3>
                    <p className="text-sm text-muted-foreground">
                      Smart recommendations based on competitor OTA data, occupancy, and room variants
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" className="gap-2" onClick={() => setAnalysisMappingDialogOpen(true)}>
                    <Link2 className="h-4 w-4" />
                    Map Rooms
                    {analysisRoomMappings.length > 0 && (
                      <Badge variant="secondary" className="ml-1 text-xs">
                        {analysisRoomMappings.length}
                      </Badge>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="gap-2" 
                    onClick={fetchPricingData}
                    disabled={isLoadingPricingData}
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoadingPricingData ? "animate-spin" : ""}`} />
                    {isLoadingPricingData ? "Loading..." : "Refresh Data"}
                  </Button>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-end gap-4">
                {/* <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Start Date
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[180px] justify-start text-left font-normal">
                        {format(startDate, 'dd-MM-yyyy')}
                        <Calendar className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => date && setStartDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div> */}

                {/* <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Bed className="h-4 w-4" />
                    Room Type
                  </label>
                  <Select value={roomType} onValueChange={setRoomType}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Room Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Room Types</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="deluxe">Deluxe</SelectItem>
                      <SelectItem value="suite">Suite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Sparkles className="h-4 w-4" />
                    Room Variant
                  </label>
                  <Select value={roomVariant} onValueChange={setRoomVariant}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Variants" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Variants</SelectItem>
                      {availableVariants.map(v => (
                        <SelectItem key={v.id} value={v.id.toString()}>
                          {v.name} ({v.roomTypeName})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Filter className="h-4 w-4" />
                    OTA Channel
                  </label>
                  <Select value={otaChannel} onValueChange={setOtaChannel}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All OTAs (Average)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All OTAs (Average)</SelectItem>
                      {otaChannels.map(channel => (
                        <SelectItem key={channel.id} value={channel.id}>{channel.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div> */}

                <div className="flex items-center gap-2 ml-auto">
                  <div className="flex rounded-md border">
                    <Button
                      variant={viewMode === 'heatmap' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="rounded-r-none gap-1.5"
                      onClick={() => setViewMode('heatmap')}
                    >
                      <Grid3X3 className="h-4 w-4" />
                      Heatmap
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="rounded-l-none gap-1.5"
                      onClick={() => setViewMode('table')}
                    >
                      <Table className="h-4 w-4" />
                      Table
                    </Button>
                  </div>
                  <Badge variant="outline" className="px-3 py-1.5">{numberOfDays} Days</Badge>
                  <Badge variant="outline" className="px-3 py-1.5">{activeCompetitors.length} Competitors</Badge>
                  <Badge variant="secondary" className="px-3 py-1.5">{selectedDates.length} Selected</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warning if no active competitors */}
          {activeCompetitors.length === 0 && (
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardContent className="py-4">
                <p className="text-sm font-medium text-amber-600">No Active Competitors</p>
                <p className="text-sm text-muted-foreground">
                  Add and activate competitors in the "Manage Competitors" tab to see pricing analysis.
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* OTA Channel Legend */}
          {otaChannel === 'all' && (
            <Card className="border-border bg-muted/20">
              <CardContent className="py-3">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium mr-2">OTA Channel Prices:</span>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-blue-600" />
                      <span className="text-xs">Booking.com</span>
                    </div>
                    {/* <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-yellow-600" />
                      <span className="text-xs">Make My Trip</span>
                    </div> */}
                    {/* <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-red-600" />
                      <span className="text-xs">Hotels.com</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-pink-600" />
                      <span className="text-xs">Airbnb</span>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      <span className="text-xs text-green-600 font-medium">+$20</span>
                      <span className="text-xs">=</span>
                      <span className="text-xs">$20 higher than your price</span>
                    </div> */}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Price Comparison Chart */}
          <PriceComparisonChart data={pricingAnalysisData} numberOfDays={numberOfDays} />

          {/* Bulk Actions */}
          <Card>
            <CardContent className="py-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">Bulk Actions:</span>
                <Button variant="outline" size="sm" onClick={handleAcceptAll} className="gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Accept All
                </Button>
                <Button variant="outline" size="sm" onClick={handleRejectAll} className="gap-1.5">
                  <XCircle className="h-4 w-4 text-destructive" />
                  Reject All
                </Button>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-green-600">{acceptedCount} accepted</span>
                <span className="text-destructive">{rejectedCount} rejected</span>
                <span className="text-muted-foreground">{15 - acceptedCount - rejectedCount} pending</span>
              </div>
            </CardContent>
          </Card>

          {/* Heatmap or Table View */}
          {viewMode === 'heatmap' ? (
            <CompetitorHeatmapGrid
              competitors={competitors}
              basePrice={basePrice}
              startDate={startDate}
              roomVariant={roomVariant !== 'all' ? roomVariant : undefined}
              otaChannel={otaChannel !== 'all' ? otaChannel : undefined}
              onAccept={handleAcceptSuggestion}
              onReject={handleRejectSuggestion}
              onAcceptAll={handleAcceptAll}
              onRejectAll={handleRejectAll}
              suggestionStatuses={suggestionStatuses}
              selectedDates={selectedDates}
              onToggleDateSelection={toggleDateSelection}
              onSelectAllDates={setSelectedDates}
              isLoading={isLoadingPricingData}
              onRefresh={fetchPricingData}
              roomMappings={analysisRoomMappings}
              onOpenMapping={() => setAnalysisMappingDialogOpen(true)}
              pricingAnalysisData={pricingAnalysisData}
            />
          ) : (
            /* Pricing Analysis Table */
            <Card>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedDates.length === pricingAnalysisData.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedDates(pricingAnalysisData.map(r => r.date));
                          } else {
                            setSelectedDates([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead className="text-right">Your Price</TableHead>
                    {/* OTA Channel Columns */}
                    {otaChannel === 'all' && (
                      <>
                        <TableHead className="text-right bg-blue-50/50">
                          <div className="flex items-center justify-end gap-1">
                            <div className="w-2 h-2 rounded-full bg-blue-600" />
                            <span>Booking</span>
                          </div>
                        </TableHead>
                        {/* <TableHead className="text-right bg-yellow-50/50">
                          <div className="flex items-center justify-end gap-1">
                            <div className="w-2 h-2 rounded-full bg-yellow-600" />
                            <span>Make My Trip</span>
                          </div>
                        </TableHead> */}
                        {/* <TableHead className="text-right bg-red-50/50">
                          <div className="flex items-center justify-end gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-600" />
                            <span>Hotels</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-right bg-pink-50/50">
                          <div className="flex items-center justify-end gap-1">
                            <div className="w-2 h-2 rounded-full bg-pink-600" />
                            <span>Airbnb</span>
                          </div>
                        </TableHead> */}
                      </>
                    )}
                    <TableHead className="text-right">Market Avg</TableHead>
                    <TableHead className="text-right">Suggested</TableHead>
                    <TableHead className="text-right">Occupancy</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                    <TableHead className="text-center">Market</TableHead>
                    <TableHead className="text-center">Strategy</TableHead>
                    <TableHead className="text-center">Action</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricingAnalysisData.map((row) => (
                    <>
                      <TableRow
                        key={row.date}
                        className={cn(
                          'cursor-pointer transition-colors',
                          selectedDates.includes(row.date) && 'bg-muted/50',
                          row.suggestionStatus === 'accepted' && 'bg-green-50/50',
                          row.suggestionStatus === 'rejected' && 'bg-red-50/30'
                        )}
                        onClick={() => toggleRowExpansion(row.date)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedDates.includes(row.date)}
                            onCheckedChange={() => toggleDateSelection(row.date)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{row.date}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-normal">{row.dayOfWeek}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatPrice(row.yourPrice)}</TableCell>

                        {/* OTA Channel Prices */}
                        {otaChannel === 'all' && (
                          <>
                            {/* Booking.com Price */}
                            <TableCell className="text-right bg-blue-50/50">
                              {row.channelPrices?.bookingCom > 0 ? (
                                <div>
                                  <span className="font-medium">{formatPrice(row.channelPrices.bookingCom)}</span>
                                  <span className={cn(
                                    'ml-1 text-xs',
                                    row.channelDiff.bookingCom > 0 ? 'text-green-600' : row.channelDiff.bookingCom < 0 ? 'text-destructive' : ''
                                  )}>
                                    {row.channelDiff.bookingCom > 0 ? '+' : ''}{row.channelDiff.bookingCom.toFixed(2)}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>

                
                          </>
                        )}

                        {/* Market Average */}
                        <TableCell className="text-right">
                          <span className={cn(
                            'font-medium',
                            row.marketAvgDiff > 0 ? 'text-destructive' : 'text-green-600'
                          )}>
                            {formatPrice(row.marketAvg)}
                          </span>
                          <span className={cn(
                            'ml-1 text-xs',
                            row.marketAvgDiff > 0 ? 'text-destructive' : 'text-green-600'
                          )}>
                            ({row.marketAvgDiff > 0 ? '+' : ''}{formatPrice(Math.abs(row.marketAvgDiff))})
                          </span>
                          <ChevronDown className="inline-block h-3 w-3 ml-1 text-muted-foreground" />
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-semibold">{formatPrice(row.suggestedPrice)}</span>
                        </TableCell>
                        <TableCell className="text-right">{row.occupancy}%</TableCell>
                        <TableCell className="text-right">
                          <span className={cn(
                            'font-medium',
                            row.change > 0 ? 'text-green-600' : row.change < 0 ? 'text-destructive' : ''
                          )}>
                            {row.change > 0 ? '+' : ''}{formatPrice(Math.abs(row.change))}
                          </span>
                          <span className={cn(
                            'ml-1 text-xs',
                            row.changePercent > 0 ? 'text-green-600' : row.changePercent < 0 ? 'text-destructive' : ''
                          )}>
                            ({row.changePercent > 0 ? '+' : ''}{row.changePercent.toFixed(1)}%)
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant="outline"
                            className={cn(
                              'font-normal text-xs',
                              row.marketStatus === 'underpriced' && 'border-green-500 text-green-600 bg-green-50',
                              row.marketStatus === 'overpriced' && 'border-red-500 text-red-600 bg-red-50',
                              row.marketStatus === 'aligned' && 'border-muted text-muted-foreground'
                            )}
                          >
                            {row.marketStatus === 'underpriced' && <ArrowDown className="h-3 w-3 mr-1" />}
                            {row.marketStatus === 'overpriced' && <ArrowUp className="h-3 w-3 mr-1" />}
                            {row.marketStatus === 'aligned' && <Minus className="h-3 w-3 mr-1" />}
                            {row.marketStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            'flex items-center justify-center gap-1 text-sm font-medium',
                            row.strategy === 'increase' && 'text-green-600',
                            row.strategy === 'decrease' && 'text-destructive'
                          )}>
                            {row.strategy === 'increase' && <TrendingUp className="h-4 w-4" />}
                            {row.strategy === 'decrease' && <TrendingDown className="h-4 w-4" />}
                            {row.strategy}
                          </span>
                        </TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant={row.suggestionStatus === 'accepted' ? 'default' : 'ghost'}
                              size="icon"
                              className={cn(
                                'h-7 w-7',
                                row.suggestionStatus === 'accepted' && 'bg-green-600 hover:bg-green-700'
                              )}
                              onClick={() => handleAcceptSuggestion(row.date)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant={row.suggestionStatus === 'rejected' ? 'destructive' : 'ghost'}
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => handleRejectSuggestion(row.date)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          {expandedRow === row.date ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedRow === row.date && (
                        <TableRow>
                          <TableCell colSpan={12} className="p-0">
                            <div className="p-4 space-y-4">
                              {/* AI Analysis Section */}
                              <Card className="border border-muted bg-white">
                                <CardContent className="p-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="h-5 w-5 text-amber-500" />
                                    <span className="font-medium">AI Analysis:</span>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{row.aiReasoning || 'Market conditions stable'}</p>
                                </CardContent>
                              </Card>

                              {/* OTA Breakdown for each competitor */}
                              <CompetitorBreakdown row={row} getOTABreakdownForCompetitors={getOTABreakdownForCompetitors} formatPrice={formatPrice} />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
          
          {/* Apply Price Changes Footer */}
          <Card className="border-t">
            <CardContent className="py-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Select dates to apply price changes</p>
              <Button 
                disabled={selectedDates.length === 0} 
                className="gap-2"
                onClick={handleApplyPriceChanges}
              >
                <TrendingUp className="h-4 w-4" />
                Apply Price Changes
              </Button>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Manage Competitors</h3>
            <Button 
              onClick={fetchCompetitorsData}
              variant="outline"
              size="sm"
              disabled={isLoadingCompetitors}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingCompetitors ? 'animate-spin' : ''}`} />
              {isLoadingCompetitors ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
          {isLoadingCompetitors ? (
            <div className="flex h-40 w-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading competitors...</span>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {competitors.map((competitor) => (
                <CompetitorCard
                  key={competitor.id}
                  competitor={competitor}
                  otaChannels={otaChannels}
                  onEdit={() => handleEditCompetitor(competitor)}
                  onDelete={() => handleDeleteCompetitor(competitor.id)}
                  onToggle={() => handleToggleCompetitor(competitor.id)}
                />
              ))}
              <div className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card hover:border-primary/50 hover:bg-primary/5 transition-all duration-200">
                <Button variant="ghost" size="lg" className="h-full w-full flex-col gap-4 rounded-xl" onClick={() => {
                  setEditingCompetitor(null);
                  setAddCompetitorOpen(true);
                }}>
                  <Plus className="h-8 w-8 text-primary" />
                  <span className="font-medium">Add Competitor</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <AddCompetitorDialog
        open={addCompetitorOpen}
        onOpenChange={(open) => {
          setAddCompetitorOpen(open);
          if (!open) setEditingCompetitor(null);
        }}
        onSave={editingCompetitor ? handleUpdateCompetitor : handleAddCompetitor}
        otaChannels={otaChannels}
        editingCompetitor={editingCompetitor}
        branches={[{ id: branchId, cm_property_id: propertyId, name: propertyName }]}
        currentPropertyId={propertyId}
      />

      <AnalysisRoomMappingDialog
        open={analysisMappingDialogOpen}
        onOpenChange={setAnalysisMappingDialogOpen}
        existingMappings={analysisRoomMappings}
        onSaveMappings={handleSaveMappings}
        propertyId={propertyId}
        branchId={branchId}
        apiCompetitorRooms={competitorRoomsForMapping}
      />
    </div>
  );
}
